import View from './view'

export default class StackController {
	constructor({ rootSelector, adapter, contentLoader, lenis, services = {} }) {
		this.root = document.querySelector(rootSelector)
		this.adapter = adapter
		this.contentLoader = contentLoader
		this.lenis = lenis || null
		this.services = services
		this.isScrollBlocked = false

		this.registry = new Map()
		this.stack = []
		this.onClick = this.onClick.bind(this)
		this.onPop = this.onPop.bind(this)
	}

	register(type, configOrClass) {
		this.registry.set(type, configOrClass)
	}

	mount() {
		if (!this.root) return
		document.addEventListener('click', this.onClick, true)

		this.adapter?.onPopState?.(this.onPop)
	}

	unmount() {
		document.removeEventListener('click', this.onClick)
		this.adapter?.offPopState?.(this.onPop)
		this.releaseScrollBlocking()
	}

	onClick(e) {
		const link = e.target.closest('[data-open-in-view]')
		if (!link) return

		const type = link.getAttribute('data-open-in-view')
		const url = link.getAttribute('href')

		if (!type) return

		e.preventDefault()
		e.stopPropagation()

		if (!url) {
			this.open({ type, url: null, static: true })
			return
		}

		this.open({ type, url })
	}

	// onClick(e) {
	// 	const link = e.target.closest('[data-open-in-view]')
	// 	if (!link) return

	// 	const type = link.getAttribute('data-open-in-view')
	// 	const url = link.getAttribute('href')

	// 	if (!type || !url) return

	// 	e.preventDefault()

	// 	this.open({ type, url })
	// }

	openStatic(type, data = {}) {
		return this.open({ type, url: null, data, static: true })
	}
	async open({
		type,
		viewId = null,
		url,
		data = {},
		static: forceStatic = false,
		fromPop = false,
		baseIndex: forcedBaseIndex,
		suppressHistory = false,
		historyUrl: forcedHistoryUrl = null,
	}) {
		const configOrClass = this.registry.get(type)
		if (!configOrClass) {
			console.warn('[ViewManager] missing registry entry:', type)
			return
		}

		let template = null
		if (viewId) {
			template = this.root.querySelector(`template[data-view-id="${viewId}"][data-view-type="${type}"]`)
		}
		if (!template) {
			template = this.root.querySelector(`template[data-view-template="${type}"]`)
		}
		if (!template) {
			console.warn('[ViewManager] missing template:', { type, viewId })
			return
		}

		// const template = this.root.querySelector(`template[data-view-template="${type}"]`)
		// if (!template) {
		// 	console.warn('[ViewManager] missing template:', type)
		// 	return
		// }

		const wrapper = document.createElement('div')
		wrapper.appendChild(template.content.cloneNode(true))
		const viewEl = wrapper.firstElementChild
		if (!viewEl) {
			console.warn('[ViewManager] template has no root element:', type)
			return
		}

		const isClass = typeof configOrClass === 'function'
		const config = isClass ? {} : configOrClass
		const isStatic = forceStatic || config?.static === true || config?.loadContent === false || !url
		const blocksScroll = config?.blockScroll === true

		const ViewClass = isClass ? configOrClass : View
		const previousUrl = this.adapter?.getUrl?.() || window.location.href
		const historyUrl = !isStatic && url ? url : forcedHistoryUrl || config?.historyUrl || previousUrl

		const view = new ViewClass({
			type,
			element: viewEl,
			config,
			lenis: this.lenis,
			services: this.services,
		})

		view.data = data

		const baseUrl = previousUrl.split('#')[0]
		const baseIndex = typeof forcedBaseIndex === 'number' ? forcedBaseIndex : (this.adapter?.getIndex?.() ?? 0)

		// Decide if this view should live in history
		const shouldHistory = (!isStatic && !!url) || (isStatic && config?.history === true)

		const hashDriven = suppressHistory && typeof historyUrl === 'string' && historyUrl.startsWith('#')

		this.stack.push({
			view,
			previousUrl,
			baseUrl,
			url,
			baseIndex,
			history: shouldHistory,
			historyUrl,
			hashDriven,
			blocksScroll,
		})
		this.root.appendChild(viewEl)

		view.mount()

		this.syncScrollBlocking(viewEl)

		if (!isStatic && url) {
			const html = await this.contentLoader.load(url)
			view.setContent(html)
		}

		await view.open()

		if (shouldHistory && !fromPop && !suppressHistory) {
			this.adapter?.push?.(historyUrl, {
				view: { type, viewId: viewId ?? null, url: url ?? null, static: isStatic, baseIndex },
			})
		}

		// if (shouldHistory && !fromPop) {
		// 	console.log('[ViewManager] pushing to history:', historyUrl, { type, url: url ?? null, static: isStatic, baseIndex })
		// 	this.adapter?.push?.(historyUrl, {
		// 		view: { type, url: url ?? null, static: isStatic, baseIndex },
		// 	})
		// }
	}

	async closeTop({ fromPop = false } = {}) {
		const item = this.stack.pop()

		if (!item) return

		await item.view.close()
		item.view.unmount()
		item.view.element.remove()

		this.syncScrollBlocking()

		// If opened via hash without history entry, clear the hash
		if (item.hashDriven && !fromPop) {
			this.adapter?.replace?.(item.baseUrl, window.history.state || {})
			return
		}

		if (item.history && !fromPop) {
			const currentIndex = this.adapter?.getIndex?.() ?? 0
			const delta = item.baseIndex - currentIndex
			if (delta !== 0) this.adapter?.go?.(delta)
		}
	}
	// async closeTop({ fromPop = false } = {}) {
	// 	const item = this.stack.pop()
	// 	if (!item) return

	// 	await item.view.close()
	// 	item.view.unmount()
	// 	item.view.element.remove()

	// 	if (item.history && !fromPop) {
	// 		const currentIndex = this.adapter?.getIndex?.() ?? 0
	// 		const delta = item.baseIndex - currentIndex
	// 		if (delta !== 0) this.adapter?.go?.(delta)
	// 	}
	// }

	syncScrollBlocking(lockTarget = null) {
		const shouldBlock = this.stack.some((item) => item.blocksScroll === true)
		if (shouldBlock === this.isScrollBlocked) return

		const browser = this.services?.browser

		if (shouldBlock) {
			if (browser?.disableScroll) {
				browser.disableScroll(lockTarget || this.root)
			} else {
				this.lenis?.stop?.()
				document.documentElement.classList.add('is-view-open')
			}
		} else if (browser?.enableScroll) {
			browser.enableScroll()
		} else {
			this.lenis?.start?.()
			document.documentElement.classList.remove('is-view-open')
		}

		this.isScrollBlocked = shouldBlock
	}

	releaseScrollBlocking() {
		if (!this.isScrollBlocked) return

		const browser = this.services?.browser
		if (browser?.enableScroll) {
			browser.enableScroll()
		} else {
			this.lenis?.start?.()
			document.documentElement.classList.remove('is-view-open')
		}

		this.isScrollBlocked = false
	}

	async onPop() {
		const state = window.history.state
		const viewState = state?.view

		if (viewState?.type) {
			const top = this.stack[this.stack.length - 1]
			if (top?.url !== viewState.url) {
				await this.open({
					type: viewState.type,
					viewId: viewState.viewId ?? null,
					url: viewState.url ?? null,
					static: viewState.static === true,
					fromPop: true,
					baseIndex: viewState.baseIndex,
				})
			}
			return
		}

		if (this.stack.length > 0) {
			await this.closeTop({ fromPop: true })
		}
	}
}
